import React, { ChangeEvent, useMemo } from 'react';
import { uniqBy } from 'lodash';
import { SelectProps, DomainObject, useEventCallback } from '@eas/common-web';
import MuiSelect from '@material-ui/core/Select';
import MuiMenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import { useStyles } from './protected-select-styles';

export function protectedSelectFactory<OPTION extends DomainObject>({
  variant,
  classesOverrides,
}: {
  variant?: 'filled' | 'outlined' | 'standard' | undefined;
  classesOverrides?: {
    input?: string;
    root?: string;
    list?: string;
    item?: string;
  };
}) {
  return function ProtectedSelectSelect({
    form,
    disabled,
    source,
    value,
    onChange,
    valueIsId = false,
    clearable = true,
    idMapper = (option: OPTION) => option.id,
    labelMapper = (option: OPTION) => (option as any).name,
    multiple = false,
  }: SelectProps<OPTION>) {
    // fix undefined value
    value = value ?? null;

    type VALUE = OPTION | string;
    type VALUES = OPTION[] | string[];

    const valueToId = useEventCallback((value: VALUE) => {
      return valueIsId ? (value as string) : idMapper(value as OPTION);
    });

    const valuesToIds = useEventCallback((values: VALUES) => {
      return (values as Array<OPTION | string>).map((value) =>
        valueToId(value)
      );
    });

    // in some cases the value is not included in the options, adds it at the end
    const options: OPTION[] = useMemo(() => {
      if (value && !valueIsId) {
        if (multiple) {
          return uniqBy([...source.items, ...(value as OPTION[])], valueToId);
        } else {
          return uniqBy([...source.items, value as OPTION], valueToId);
        }
      } else {
        return source.items;
      }
    }, [value, source, valueIsId, valueToId, multiple]);

    const idToValue = useEventCallback((id: string) => {
      return valueIsId
        ? id
        : options.find((option) => idMapper(option) === id)!;
    });

    const idsToValues = useEventCallback((ids: string[]) => {
      return ids.map((id) => idToValue(id)) as VALUES;
    });

    const handleChange = useEventCallback(
      async (e: ChangeEvent<{ name?: string; value: unknown }>) => {
        const ids = e.target.value;

        let values: VALUE | VALUES | null;
        if (ids === undefined || ids === '') {
          values = null;
        } else if (multiple) {
          values = idsToValues(ids as string[]);
        } else {
          values = idToValue(ids as string);
          if (!valueIsId) {
            values = await source.loadDetail(values as any);
          }
        }

        onChange(values);
      }
    );

    const handleClear = useEventCallback(() => {
      onChange(null);
    });

    function getLocalValue(value: VALUE | VALUE[] | null) {
      if (!value) {
        if (multiple) {
          return [];
        } else {
          return '';
        }
      }

      const optionIds = options.map((option) => idMapper(option));

      if (multiple) {
        const ids = valuesToIds(value as VALUES);
        return ids.filter((id) => optionIds.includes(id));
      } else {
        const id = valueToId(value as VALUE);
        return optionIds.includes(id) ? id : '';
      }
    }

    const { root, input, list, item, adornment } = useStyles();

    const showClearButton = clearable && !disabled && value !== null;

    return (
      <>
        <MuiSelect
          endAdornment={
            <InputAdornment position="end" classes={{ root: adornment }}>
              {showClearButton && (
                <IconButton size="small" onClick={handleClear}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          }
          inputProps={{
            form,
          }}
          className={classesOverrides?.input ?? input}
          classes={{
            root: classesOverrides?.root ?? root,
          }}
          multiple={multiple}
          variant={variant}
          disabled={disabled}
          value={getLocalValue(value)}
          onChange={handleChange}
          autoWidth={true}
          MenuProps={{
            classes: { list: classesOverrides?.list ?? list },
            anchorOrigin: {
              horizontal: 'left',
              vertical: 'bottom',
            },
            getContentAnchorEl: null,
          }}
        >
          {options.map((option) => (
            <MuiMenuItem
              key={idMapper(option)}
              value={idMapper(option)}
              classes={{ root: classesOverrides?.item ?? item }}
            >
              {labelMapper(option)}
            </MuiMenuItem>
          ))}
        </MuiSelect>
      </>
    );
  };
}
